import { manufacturingFetch } from "@/lib/manufacturingClient";
import { resizeQcPhoto } from "@/lib/manufacturing/qcPhotoResize";
import { requireSupabase } from "@/lib/supabase";
import type {
  BuilderAssistantBundle,
  BuilderAssistantComponent,
  BuilderAssistantDraft,
  BuilderAssistantModelMatch,
  BuilderAssistantObservation,
  BuilderAssistantSerialMatch,
  BuilderAssistantSession,
  BuilderAssistantSummary,
} from "@/lib/manufacturing/builderAssistantTypes";

const PATH = "/api/mfg/builder-assistant";

function post<T>(body: Record<string, unknown>) {
  return manufacturingFetch<T>(PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function seedBuilderAssistant() {
  return post<{ ok: true; components: BuilderAssistantComponent[] }>({
    op: "seed",
  });
}

export function resolveBuilderAssistantSerial(query: string) {
  return post<{ ok: true; matches: BuilderAssistantSerialMatch[] }>({
    op: "resolve-serial",
    query,
  });
}

export function resolveBuilderAssistantModel(query: string) {
  return post<{ ok: true; matches: BuilderAssistantModelMatch[] }>({
    op: "resolve-model",
    query,
  });
}

export function confirmBuilderAssistantSession(unitId: string, modelHint: string, longSerial = "") {
  return post<{ ok: true; session: BuilderAssistantSession }>({
    op: "confirm-session",
    unit_id: unitId,
    model_hint: modelHint,
    long_serial: longSerial,
  });
}

export type WaitingInspectionOrder = {
  unit_id: string;
  serial: string;
  long_serial: string;
  model: string;
  size: string;
};

export function listWaitingInspectionOrders() {
  return post<{ ok: true; orders: WaitingInspectionOrder[] }>({ op: "list-waiting-orders" });
}

export function startUnmatchedBuilderAssistantSession(input: {
  serialText: string;
  modelHint: string;
  recipeId?: string | null;
  longSerial?: string;
}) {
  return post<{ ok: true; session: BuilderAssistantSession }>({
    op: "start-unmatched",
    serial_text: input.serialText,
    model_hint: input.modelHint,
    recipe_id: input.recipeId ?? null,
    long_serial: input.longSerial ?? "",
  });
}

export function linkBuilderAssistantSession(input: {
  sessionId: string;
  unitId?: string | null;
  recipeId?: string | null;
  serialText?: string;
  modelHint?: string;
}) {
  return post<{ ok: true; session: BuilderAssistantSession }>({
    op: "link-session",
    session_id: input.sessionId,
    unit_id: input.unitId ?? null,
    recipe_id: input.recipeId ?? null,
    serial_text: input.serialText,
    model_hint: input.modelHint,
  });
}

export function getBuilderAssistantSession(sessionId: string) {
  return post<BuilderAssistantBundle & { ok: true }>({ op: "get-session", session_id: sessionId });
}

export type OpenQcItem = {
  id: string;
  description: string;
  serial_text: string | null;
  session_id: string;
  category_id: string | null;
  category_name: string | null;
  item_title: string | null;
};

export type QcRosterPerson = {
  user_id: string;
  name: string;
  email: string | null;
  email_opted_in: boolean;
};

export function listOpenQcItems() {
  return post<{ ok: true; items: OpenQcItem[] }>({ op: "list-open-qc" });
}

export function listQcMailLists() {
  return post<{
    ok: true;
    failure: QcMailPerson[];
    ready: QcMailPerson[];
    members: QcMailMember[];
    approved_domains: string[];
    ready_error: string | null;
    address_error: string | null;
  }>({ op: "list-qc-mail-lists" });
}

export function setQcMailRecipient(userId: string, kind: "failure" | "ready", optedIn: boolean) {
  return post<{ ok: true }>({ op: "set-qc-mail-recipient", user_id: userId, kind, opted_in: optedIn });
}

export function setQcMailAddress(email: string, kind: "failure" | "ready", optedIn = true) {
  return post<{ ok: true }>({ op: "set-qc-mail-address", email, kind, opted_in: optedIn });
}

export function addQcMailDomain(domain: string) {
  return post<{ ok: true; domain: string }>({ op: "add-qc-mail-domain", domain });
}

export type QcMailPerson = {
  user_id: string | null;
  name: string;
  email: string | null;
  opted_in: boolean;
  kind?: "user" | "address";
};

export type QcMailMember = {
  user_id: string;
  name: string;
  email: string | null;
};

export function listQcRoster() {
  return post<{ ok: true; people: QcRosterPerson[] }>({ op: "list-qc-roster" });
}

export function readQcEmailOptIn() {
  return post<{ ok: true; opted_in: boolean }>({ op: "qc-email-opt-in" });
}

export function setQcEmailOptIn(optedIn: boolean) {
  return post<{ ok: true; opted_in: boolean }>({ op: "qc-email-opt-in", opted_in: optedIn });
}

export function listBuilderAssistantSessions() {
  return post<{ ok: true; sessions: BuilderAssistantSession[] }>({ op: "list-sessions" });
}

export function continueBuilderAssistantSession(sessionId: string) {
  return post<{ ok: true; session: BuilderAssistantSession }>({
    op: "continue-session",
    session_id: sessionId,
  });
}

export function extractBuilderAssistantObservation(sessionId: string, transcript: string) {
  return post<{ ok: true; draft: BuilderAssistantDraft; observation?: BuilderAssistantObservation }>({
    op: "extract-observation",
    session_id: sessionId,
    transcript,
  });
}

export function saveBuilderAssistantObservation(input: {
  session_id: string;
  observation_id?: string;
  description: string;
  observation_type: string;
  component_id?: string | null;
  severity?: string | null;
  crew_label?: string | null;
  tag_keys: string[];
  row_status: "pending" | "saved";
}) {
  return post<{ ok: true; observation: BuilderAssistantObservation }>({
    op: "save-observation",
    ...input,
    draft: {
      description: input.description,
      observation_type: input.observation_type,
      component_name: null,
      severity: input.severity ?? null,
      crew_label: input.crew_label ?? null,
      suggested_tags: input.tag_keys,
    },
  });
}

export function submitBuilderAssistantSession(sessionId: string) {
  return post<{ ok: true; session: BuilderAssistantSession; summary: BuilderAssistantSummary }>({
    op: "submit-session",
    session_id: sessionId,
  });
}

export function listBuilderAssistantCandidates() {
  return post<{ ok: true; observations: BuilderAssistantObservation[] }>({ op: "list-candidates" });
}

export function markBuilderAssistantCandidateReviewed(observationId: string) {
  return post<{ ok: true; observation: BuilderAssistantObservation }>({
    op: "mark-reviewed",
    observation_id: observationId,
  });
}

export function renameBuilderAssistantComponent(componentId: string, name: string) {
  return post<{ ok: true }>({ op: "rename-component", component_id: componentId, name });
}

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  attachment_id: string;
  content_type: string;
};

export function markBuilderAssistantObservationFixed(input: {
  observation_id: string;
  description: string;
  fixed_by_name: string;
}) {
  return post<{ ok: true; observation: BuilderAssistantObservation }>({
    op: "mark-fixed",
    ...input,
  });
}

export async function uploadBuilderAssistantPhoto(
  observationId: string,
  file: File,
  caption?: "fix",
): Promise<{ ok: true; cdn_url: string } | { ok: false; error: string }> {
  const prepared = await resizeQcPhoto(file);
  const sb = requireSupabase();
  const contentType = (prepared.type || "image/jpeg").toLowerCase();
  const sign = await post<SignOk>({
    op: "sign-evidence",
    observation_id: observationId,
    content_type: contentType,
    byte_size: prepared.size,
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  const signed = sign.data;
  if (!signed?.path || !signed.token || !signed.bucket) {
    return { ok: false, error: "Photo upload could not be signed." };
  }
  const { error: upErr } = await sb.storage.from(signed.bucket).uploadToSignedUrl(
    signed.path,
    signed.token,
    prepared,
    { contentType: signed.content_type || contentType, upsert: false },
  );
  if (upErr) return { ok: false, error: upErr.message };
  const commit = await post<{ ok: true; cdn_url: string }>({
    op: "commit-evidence",
    observation_id: observationId,
    attachment_id: signed.attachment_id,
    object_path: signed.path,
    content_type: signed.content_type || contentType,
    byte_size: prepared.size,
    caption: caption ?? "",
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true, cdn_url: commit.data.cdn_url };
}

export function deleteBuilderAssistantPhoto(evidenceId: string) {
  return post<{ ok: true }>({ op: "delete-evidence", evidence_id: evidenceId });
}
